/**
 * Camera Raw / Lightroom Classic preset evaluation.
 *
 * Takes a preset in Adobe's own `crs:` vocabulary — the attribute set lifted
 * verbatim out of a `.xmp` by `tools/xmp-to-preset.mjs` — and compiles it into a
 * plain `(r, g, b) -> [r, g, b]` function over display-referred sRGB. The only
 * consumer is {@link module:fx/FilmLookPass}, which evaluates it over a lattice
 * once at startup and ships the result to the GPU as a 3D LUT; nothing here runs
 * per frame, so it is written for clarity over speed.
 *
 * ── What this can and cannot be ──────────────────────────────────────────────
 * Lightroom applies a preset to *scene-referred raw sensor data* in a wide
 * linear working space, through a camera profile (a `.dcp`) that the preset only
 * names. We have none of that: by the time a composer pass runs, the frame is an
 * already-tone-mapped sRGB image, and `crs:CameraProfile="Portra 160+1"` is a
 * binary lookup table we cannot read. So this is a *grade in the style of* the
 * preset, not a bit-exact port — the parameters are real and applied in a
 * plausible order, but the stage each one acts on is an approximation.
 *
 * Two approximations are worth naming because they are the ones most likely to
 * show. First, the basic-tone sliders (Highlights/Shadows/Whites/Blacks) are
 * global region-weighted curves applied per channel; the real ones are
 * luminance-based and partly *local*, which is exactly the part a LUT cannot
 * express — a LUT maps a colour to a colour with no knowledge of its neighbours.
 * Second, every slider→effect scaling in RESPONSE below is fitted by eye rather
 * than derived, since Adobe publishes none of them. Those constants are the
 * tuning dials if a converted preset lands too strong or too weak.
 *
 * Order of operations follows the Develop module's panel order — calibration
 * first (it belongs to the profile stage, not the panel's position at the
 * bottom of the list), then basic tone, tone curves, HSL, split toning.
 */

// ── Slider response ─────────────────────────────────────────────────────────
// How far a slider at ±100 actually moves the image. Not published by Adobe;
// fitted against reference renders and the single place to retune if a
// converted preset comes out heavier or lighter than Lightroom's own render.
const RESPONSE = {
  calibHueDeg:   30,    // calibration Red/Green/BlueHue at 100 → primary rotated 30°
  // Calibration Red/Green/BlueSaturation, as a scale on the slider. This one is
  // known to be over-strong: desaturating a primary mixes grey into it, the
  // white-preserving normalisation in calibrationMatrix then scales the
  // diagonal down to compensate, and the two compound across all three
  // primaries. Portra's modest -10/-10/-15 costs ~20 points of global
  // saturation at 1.0, far more than those sliders do in Lightroom. Dropping
  // this to ~0.3 is closer, at the price of a few degrees of hue drift (most
  // scene colours move under 5°, blues about 7°). Left at 1.0 so the look does
  // not move on its own — it is a fidelity dial, not an artistic one, which is
  // why it lives here and not in SimConfig's FILM_LOOK.adjust.
  calibSat:      1.0,
  blackWhite:    0.25,  // Blacks/Whites at 100 → endpoint moved a quarter of the range
  shadowHigh:    0.20,  // Shadows/Highlights at 100 → region moved a fifth of the range
  parametric:    0.20,  // parametric slider at 100 → region control point moved a fifth
  hslHueDeg:     30,    // HSL hue at 100 → band rotated 30° (orange → yellow)
  hslSaturation: 1.0,   // HSL saturation at ±100 → ×0 … ×2
  hslLuminance:  1.0,   // HSL luminance at ±100 → ×0 … ×2
  splitTone:     0.5,   // split-toning saturation at 100 → half-strength tint
};

// HSL / Color Mixer band centres, in degrees of hue. Adobe's eight bands are
// unevenly spaced (four of them inside the first 120°), which is why the band
// weighting below interpolates between neighbours rather than using a fixed
// bandwidth.
const HSL_BANDS = [
  { name: 'Red',     hue: 0 },
  { name: 'Orange',  hue: 30 },
  { name: 'Yellow',  hue: 60 },
  { name: 'Green',   hue: 120 },
  { name: 'Aqua',    hue: 180 },
  { name: 'Blue',    hue: 240 },
  { name: 'Purple',  hue: 285 },
  { name: 'Magenta', hue: 315 },
];

// Resolution of the baked 1-D curves. A tone curve is evaluated three times per
// lattice point, so each spline is sampled once into a table and interpolated.
const CURVE_TABLE_SIZE = 1024;

// Chroma (max channel − min channel) below which a colour is treated as
// achromatic and the HSL bands are faded out. A grey has no hue to select on —
// its hue is whatever its last rounding error happened to leave highest — so
// without this the eight bands fight over the neutral axis and any difference
// between their Luminance sliders becomes a step in the greyscale ramp. This
// preset has Red −20 against Aqua 0, which put a 20% cliff in the highlights.
//
// The selector is chroma and not HSL saturation because saturation is useless
// at the ends of the lightness range: a near-white (0.95, 0.83, 0.86) scores
// S = 0.53 and would take the Red band's full −20% luminance. Chroma reads
// both near-white and near-black as hueless, which is what the eye does.
//
// The width matters as much as the behaviour. This grade is baked to a 33³
// lattice, so a feature narrower than a few times 1/32 cannot be represented
// and shows up as interpolation error — which is exactly what an abrupt
// achromatic cutoff would be, right along the neutral axis where banding is
// most visible. 0.15 spans ~5 lattice cells.
const HSL_NEUTRAL_CHROMA = 0.15;

// ── Small math helpers ──────────────────────────────────────────────────────
const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
const mix = (a, b, t) => a + (b - a) * t;
const smoothstep01 = (x) => x * x * (3 - 2 * x);
// Inverse of smoothstep01 on [0,1] — the de-contrast counterpart.
const unSmoothstep01 = (x) => 0.5 - Math.sin(Math.asin(1 - 2 * clamp01(x)) / 3);

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}
function linearToSrgb(c) {
  return c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055;
}

/** Read a `crs:` value as a number. Handles the "+30" / "-12" signed strings. */
function num(preset, key, fallback = 0) {
  const v = preset[key];
  if (v === undefined || v === null) return fallback;
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
}

// ── Curves ──────────────────────────────────────────────────────────────────

/**
 * Monotone cubic Hermite interpolation (Fritsch–Carlson). Camera Raw's point
 * curves are splines through hand-placed control points, and the one property
 * they must have is monotonicity — an overshooting spline would invert tones
 * between two control points and posterise. Fritsch–Carlson limits the tangents
 * to guarantee that, at the cost of flattening slightly at sharp corners.
 */
function monotoneSpline(xs, ys) {
  const n = xs.length;
  if (n < 2) return () => ys[0] ?? 0;

  const slope = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) slope[i] = (ys[i + 1] - ys[i]) / (xs[i + 1] - xs[i]);

  const tangent = new Array(n);
  tangent[0] = slope[0];
  tangent[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) {
    tangent[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
  }
  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) { tangent[i] = 0; tangent[i + 1] = 0; continue; }
    const a = tangent[i] / slope[i];
    const b = tangent[i + 1] / slope[i];
    const s = a * a + b * b;
    if (s > 9) {
      const tau = 3 / Math.sqrt(s);
      tangent[i] = tau * a * slope[i];
      tangent[i + 1] = tau * b * slope[i];
    }
  }

  return (x) => {
    if (x <= xs[0]) return ys[0];
    if (x >= xs[n - 1]) return ys[n - 1];
    let i = 0;
    while (i < n - 2 && x > xs[i + 1]) i++;
    const h = xs[i + 1] - xs[i];
    const t = (x - xs[i]) / h;
    const t2 = t * t;
    const t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[i]
         + (t3 - 2 * t2 + t) * h * tangent[i]
         + (-2 * t3 + 3 * t2) * ys[i + 1]
         + (t3 - t2) * h * tangent[i + 1];
  };
}

/**
 * Bake a `[0,1] -> [0,1]` function into a lookup table with linear
 * interpolation, forcing the result monotone on the way. The clamp is cheap
 * insurance: the basic-tone stage sums four independent region weights, and an
 * extreme preset could in principle sum them into a local inversion.
 */
function bakeCurve(fn) {
  const table = new Float32Array(CURVE_TABLE_SIZE);
  let prev = 0;
  for (let i = 0; i < CURVE_TABLE_SIZE; i++) {
    const v = clamp01(fn(i / (CURVE_TABLE_SIZE - 1)));
    prev = v < prev ? prev : v;
    table[i] = prev;
  }
  return (x) => {
    const p = clamp01(x) * (CURVE_TABLE_SIZE - 1);
    const i = Math.floor(p);
    if (i >= CURVE_TABLE_SIZE - 1) return table[CURVE_TABLE_SIZE - 1];
    return mix(table[i], table[i + 1], p - i);
  };
}

/**
 * Parse a `crs:ToneCurvePV2012*` sequence ("21, 29") into a baked curve, or null
 * when the preset leaves that channel alone — a curve of only its two endpoints
 * is the identity.
 */
function pointCurve(points) {
  if (!points || points.length < 3) return null;
  const xs = [];
  const ys = [];
  for (const p of points) {
    const [x, y] = Array.isArray(p) ? p : String(p).split(',').map((s) => parseFloat(s));
    xs.push(x / 255);
    ys.push(y / 255);
  }
  return bakeCurve(monotoneSpline(xs, ys));
}

/**
 * The Basic panel's tone sliders as one curve over gamma-encoded values.
 *
 * Each slider gets a weight over the tone axis and shifts the curve by its
 * amount scaled by that weight. Blacks and Whites peak *at* the endpoints, so
 * they move the black and white points themselves; Shadows and Highlights are
 * bumps that fall off at both ends, so they open or recover a region without
 * touching the endpoints. Contrast is a separate smoothstep blend about the
 * mid-point, and Exposure is the one stage that has to happen in linear light.
 */
function basicToneCurve(preset) {
  const exposure   = num(preset, 'Exposure2012');
  const contrast   = num(preset, 'Contrast2012') / 100;
  const highlights = num(preset, 'Highlights2012') / 100;
  const shadows    = num(preset, 'Shadows2012') / 100;
  const whites     = num(preset, 'Whites2012') / 100;
  const blacks     = num(preset, 'Blacks2012') / 100;

  if (!exposure && !contrast && !highlights && !shadows && !whites && !blacks) return null;

  const gain = 2 ** exposure;

  // Gaussian region weights. σ = 0.25 gives each slider roughly a quarter of the
  // range before it fades out, which keeps Shadows and Highlights from fighting
  // over the midtones while still overlapping enough to stay smooth.
  const bump = (x, centre, sigma) => Math.exp(-(((x - centre) / sigma) ** 2));

  return bakeCurve((x) => {
    let v = x;
    if (exposure) v = clamp01(linearToSrgb(srgbToLinear(v) * gain));

    if (contrast > 0)      v = mix(v, smoothstep01(v), contrast);
    else if (contrast < 0) v = mix(v, unSmoothstep01(v), -contrast);

    v += blacks     * RESPONSE.blackWhite * bump(v, 0.0,  0.25);
    v += whites     * RESPONSE.blackWhite * bump(v, 1.0,  0.25);
    v += shadows    * RESPONSE.shadowHigh * bump(v, 0.25, 0.25);
    v += highlights * RESPONSE.shadowHigh * bump(v, 0.75, 0.25);
    return v;
  });
}

/**
 * The Tone Curve panel's parametric half. The four sliders move control points
 * at the centre of each region delimited by the three splits; the spline through
 * them (plus the fixed 0 and 1 anchors) is the curve. Lightroom's splits default
 * to 25/50/75 but a preset may move them, which shifts where each slider bites.
 */
function parametricCurve(preset) {
  const shadows    = num(preset, 'ParametricShadows') / 100;
  const darks      = num(preset, 'ParametricDarks') / 100;
  const lights     = num(preset, 'ParametricLights') / 100;
  const highlights = num(preset, 'ParametricHighlights') / 100;
  if (!shadows && !darks && !lights && !highlights) return null;

  const sSplit = num(preset, 'ParametricShadowSplit', 25) / 100;
  const mSplit = num(preset, 'ParametricMidtoneSplit', 50) / 100;
  const hSplit = num(preset, 'ParametricHighlightSplit', 75) / 100;

  const centres = [sSplit / 2, (sSplit + mSplit) / 2, (mSplit + hSplit) / 2, (hSplit + 1) / 2];
  const amounts = [shadows, darks, lights, highlights];

  const xs = [0];
  const ys = [0];
  for (let i = 0; i < 4; i++) {
    xs.push(centres[i]);
    ys.push(clamp01(centres[i] + amounts[i] * RESPONSE.parametric));
  }
  xs.push(1);
  ys.push(1);

  return bakeCurve(monotoneSpline(xs, ys));
}

// ── Camera calibration ──────────────────────────────────────────────────────

/** HSV → RGB, h in degrees. Used only to build the calibration primaries. */
function hsvToRgb(h, s, v) {
  const c = v * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = v - c;
  let r = 0; let g = 0; let b = 0;
  if (hp < 1)      { r = c; g = x; }
  else if (hp < 2) { r = x; g = c; }
  else if (hp < 3) { g = c; b = x; }
  else if (hp < 4) { g = x; b = c; }
  else if (hp < 5) { r = x; b = c; }
  else             { r = c; b = x; }
  return [r + m, g + m, b + m];
}

/**
 * The Camera Calibration panel as a 3×3 matrix on linear RGB.
 *
 * Each slider pair rotates and desaturates one of the three primaries; building
 * the matrix from the rotated primaries as columns reproduces that directly.
 * The rows are then normalised to sum to 1 so the transform maps white to white
 * — without that, desaturating a primary bleeds energy into the other two
 * channels and the whole frame picks up a cast and a brightness shift.
 *
 * Returns null when the preset leaves calibration at its defaults.
 */
function calibrationMatrix(preset) {
  const params = [
    { hue: num(preset, 'RedHue'),   sat: num(preset, 'RedSaturation'),   base: 0 },
    { hue: num(preset, 'GreenHue'), sat: num(preset, 'GreenSaturation'), base: 120 },
    { hue: num(preset, 'BlueHue'),  sat: num(preset, 'BlueSaturation'),  base: 240 },
  ];
  if (params.every((p) => p.hue === 0 && p.sat === 0)) return null;

  // Columns = the adjusted primaries.
  const cols = params.map((p) => hsvToRgb(
    p.base + (p.hue / 100) * RESPONSE.calibHueDeg,
    clamp01(1 + (p.sat / 100) * RESPONSE.calibSat),
    1,
  ));

  const m = [
    [cols[0][0], cols[1][0], cols[2][0]],
    [cols[0][1], cols[1][1], cols[2][1]],
    [cols[0][2], cols[1][2], cols[2][2]],
  ];
  for (const row of m) {
    const sum = row[0] + row[1] + row[2];
    if (sum > 1e-6) { row[0] /= sum; row[1] /= sum; row[2] /= sum; }
  }
  return m;
}

// ── HSL ─────────────────────────────────────────────────────────────────────

function rgbToHsl(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d < 1e-9) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r)      h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else                h = ((r - g) / d + 4) * 60;
  return [h, s, l];
}

function hslToRgb(h, s, l) {
  if (s < 1e-9) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hk = (((h % 360) + 360) % 360) / 360;
  const channel = (t) => {
    let tt = t;
    if (tt < 0) tt += 1;
    if (tt > 1) tt -= 1;
    if (tt < 1 / 6) return p + (q - p) * 6 * tt;
    if (tt < 1 / 2) return q;
    if (tt < 2 / 3) return p + (q - p) * (2 / 3 - tt) * 6;
    return p;
  };
  return [channel(hk + 1 / 3), channel(hk), channel(hk - 1 / 3)];
}

/**
 * Blend the eight HSL band adjustments down to one per-pixel triple.
 *
 * A hue falls between two band centres; `smoothstep` across that gap gives the
 * pair of weights, so a colour sitting on a boundary gets a clean mix of its
 * two neighbours and the bands never seam. Weights always sum to 1, which is
 * what lets a preset that nudges every band produce a global shift rather than
 * a banded one.
 *
 * The whole blend then fades out below HSL_NEUTRAL_CHROMA, because hue
 * selection is meaningless on a colour that has barely any hue — see that
 * constant for what goes wrong without it.
 */
function hslAdjustments(preset) {
  const hue = HSL_BANDS.map((b) => num(preset, `HueAdjustment${b.name}`));
  const sat = HSL_BANDS.map((b) => num(preset, `SaturationAdjustment${b.name}`));
  const lum = HSL_BANDS.map((b) => num(preset, `LuminanceAdjustment${b.name}`));

  const vibrance   = num(preset, 'Vibrance') / 100;
  const saturation = num(preset, 'Saturation') / 100;

  const anyBand = [...hue, ...sat, ...lum].some((v) => v !== 0);
  if (!anyBand && !vibrance && !saturation) return null;

  const n = HSL_BANDS.length;

  return (h, s, l, chroma) => {
    // Locate the band pair bracketing this hue, wrapping Magenta → Red.
    let i = n - 1;
    for (let k = 0; k < n - 1; k++) {
      if (h >= HSL_BANDS[k].hue && h < HSL_BANDS[k + 1].hue) { i = k; break; }
    }
    const j = (i + 1) % n;
    const lo = HSL_BANDS[i].hue;
    const hi = i === n - 1 ? HSL_BANDS[0].hue + 360 : HSL_BANDS[j].hue;
    const hh = i === n - 1 && h < lo ? h + 360 : h;
    const t = smoothstep01(clamp01((hh - lo) / (hi - lo)));

    // Hue selection is only meaningful on a colour that has a hue.
    const hueWeight = smoothstep01(clamp01(chroma / HSL_NEUTRAL_CHROMA));
    const blend = (arr) => (arr[i] * (1 - t) + arr[j] * t) * hueWeight;

    const outH = h + (blend(hue) / 100) * RESPONSE.hslHueDeg;
    let outS = s * (1 + (blend(sat) / 100) * RESPONSE.hslSaturation);
    const outL = clamp01(l * (1 + (blend(lum) / 100) * RESPONSE.hslLuminance));

    // Vibrance before Saturation, and weighted by how unsaturated the colour
    // already is — that (1 - s) term is the whole difference between the two.
    if (vibrance)   outS += vibrance * (1 - clamp01(outS)) * outS;
    if (saturation) outS *= 1 + saturation;

    return [outH, clamp01(outS), outL];
  };
}

// ── Split toning ────────────────────────────────────────────────────────────

/**
 * Split Toning / Color Grading's shadow and highlight tints. `Balance` slides
 * the crossover between the two; the weighting is by luminance, so the tint
 * rides the tone rather than the hue.
 */
function splitToning(preset) {
  const shSat = num(preset, 'SplitToningShadowSaturation') / 100;
  const hiSat = num(preset, 'SplitToningHighlightSaturation') / 100;
  if (!shSat && !hiSat) return null;

  const shHue = num(preset, 'SplitToningShadowHue');
  const hiHue = num(preset, 'SplitToningHighlightHue');
  const balance = num(preset, 'SplitToningBalance') / 100;

  const shRgb = hsvToRgb(shHue, 1, 1);
  const hiRgb = hsvToRgb(hiHue, 1, 1);
  const pivot = clamp01(0.5 - balance * 0.5);

  return (r, g, b) => {
    const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const hiW = smoothstep01(clamp01((luma - pivot) / Math.max(1e-3, 1 - pivot)));
    const shW = 1 - hiW;
    const out = [r, g, b];
    for (let c = 0; c < 3; c++) {
      out[c] = mix(out[c], shRgb[c] * luma, shW * shSat * RESPONSE.splitTone);
      out[c] = mix(out[c], hiRgb[c] * luma, hiW * hiSat * RESPONSE.splitTone);
    }
    return out;
  };
}

// ── Compilation ─────────────────────────────────────────────────────────────

/**
 * Compile a preset into `(r, g, b) -> [r, g, b]` over display-referred sRGB.
 *
 * Every stage the preset leaves at its default compiles to null and is skipped
 * outright, so a preset that only ships a tone curve costs only a tone curve.
 */
export function compileGrade(preset) {
  const calib      = calibrationMatrix(preset);
  const basic      = basicToneCurve(preset);
  const parametric = parametricCurve(preset);
  const master     = pointCurve(preset.ToneCurvePV2012);
  const curveR     = pointCurve(preset.ToneCurvePV2012Red);
  const curveG     = pointCurve(preset.ToneCurvePV2012Green);
  const curveB     = pointCurve(preset.ToneCurvePV2012Blue);
  const hsl        = hslAdjustments(preset);
  const split      = splitToning(preset);

  const monochrome = preset.ConvertToGrayscale === 'True' || preset.ConvertToGrayscale === true;

  return (r0, g0, b0) => {
    let r = clamp01(r0);
    let g = clamp01(g0);
    let b = clamp01(b0);

    // 1. Camera calibration — a primary remix, so it belongs in linear light.
    if (calib) {
      const lr = srgbToLinear(r);
      const lg = srgbToLinear(g);
      const lb = srgbToLinear(b);
      r = linearToSrgb(clamp01(calib[0][0] * lr + calib[0][1] * lg + calib[0][2] * lb));
      g = linearToSrgb(clamp01(calib[1][0] * lr + calib[1][1] * lg + calib[1][2] * lb));
      b = linearToSrgb(clamp01(calib[2][0] * lr + calib[2][1] * lg + calib[2][2] * lb));
    }

    // 2. Basic panel tone.
    if (basic) { r = basic(r); g = basic(g); b = basic(b); }

    if (monochrome) {
      const y = clamp01(0.2126 * r + 0.7152 * g + 0.0722 * b);
      r = y; g = y; b = y;
    }

    // 3. Tone curve — parametric first, then the point curve's master channel,
    //    then the per-channel curves. That is the order the panel applies them,
    //    and it matters: the per-channel curves act on an already-shaped tone.
    if (parametric) { r = parametric(r); g = parametric(g); b = parametric(b); }
    if (master)     { r = master(r);     g = master(g);     b = master(b); }
    if (curveR) r = curveR(r);
    if (curveG) g = curveG(g);
    if (curveB) b = curveB(b);

    // 4. HSL / Color Mixer (+ Vibrance and Saturation, which share the stage).
    if (hsl && !monochrome) {
      const [h0, s0, l0] = rgbToHsl(r, g, b);
      const chroma = Math.max(r, g, b) - Math.min(r, g, b);
      const [h1, s1, l1] = hsl(h0, s0, l0, chroma);
      [r, g, b] = hslToRgb(h1, s1, l1);
    }

    // 5. Split toning.
    if (split) [r, g, b] = split(r, g, b);

    return [clamp01(r), clamp01(g), clamp01(b)];
  };
}

/**
 * Evaluate a compiled grade over a `size³` lattice, returning RGBA floats in the
 * layout a `THREE.Data3DTexture` wants (R fastest, then G, then B).
 */
export function buildLUTData(preset, size) {
  const grade = compileGrade(preset);
  const data = new Float32Array(size * size * size * 4);
  const step = 1 / (size - 1);

  let i = 0;
  for (let z = 0; z < size; z++) {
    const b = z * step;
    for (let y = 0; y < size; y++) {
      const g = y * step;
      for (let x = 0; x < size; x++) {
        const [cr, cg, cb] = grade(x * step, g, b);
        data[i++] = cr;
        data[i++] = cg;
        data[i++] = cb;
        data[i++] = 1;
      }
    }
  }
  return data;
}
